import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useParams } from 'react-router-dom';
import styled from 'styled-components';

import {
  Button,
  Card,
  CircularProgress,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Typography,
} from '@mui/material';
import Grid from 'material/components/Grid';
import { Add as AddIcon } from '@mui/icons-material';

import { campaignsSelectors } from 'store/selectors/campaigns';
import { fetchAdvocates, fetchCampaign, fetchCampaignTargets } from 'store/actions/campaigns';
import {
  Backdrop,
  BreadcrumbsNavigation,
  Container,
  TabPanel,
  Tabs,
  TargetsTable,
} from 'common/components';

import { CircularProgressWrapper } from 'globalStyles';

import CampaignAnalyticsReport from '../../CampaignReports/components/CampaignAnalyticsReport';
import CampaignMessagesReport from '../../CampaignReports/components/CampaignMessagesReport';
import { ReportsLayout } from '../../CampaignReports/components/styles';

import ActionCenterItem from '../components/ActionCenterItem';
import AdvocatesTable from '../components/AdvocatesTable';
import CampaignTopCard from '../components/CampaignTopCard';
import CampaignOverview from '../components/CampaignOverview';

const CampaignTableWrapper = styled.div`
  width: 100%;
  margin: 0 0 82px;
`;

const StatusCardWrapper = styled(Card)`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  border-bottom: 0;
`;

const StyledSelect = styled(Select)`
  width: 130px;
  margin: 0 30px 24px 0;
`;

const StyledSelectLabel = styled(InputLabel)`
  width: 130px;
  margin: 24px 30px 0 0;
`;

const StyledPaper = styled(Paper)`
  border-bottom: 0;
`;

const StyledTypography = styled(Typography)`
  padding: 20px 0 20px 19px;
`;

const ButtonWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`;

const AnalyticsReportWrapper = styled(Card)`
  padding: 24px;
`;

const CampaignDetails = () => {
  const dispatch = useDispatch();
  const { campaignUuid, organizationUuid } = useParams();
  const firstFetch = useRef({
    advocates: false,
    targets: false,
  });

  const {
    advocatesList,
    campaignDetails,
    campaignTargets,
    isAdvocatesFetching,
    isCampaignFetching,
    isCampaignTargetsFetching,
    isPending,
  } = useSelector(state => ({
    advocatesList: campaignsSelectors.getAdvocates(state),
    campaignDetails: campaignsSelectors.getCampaignDetails(state),
    campaignTargets: campaignsSelectors.getCampaignTargets(state),
    isAdvocatesFetching: campaignsSelectors.isAdvocatesFetching(state),
    isCampaignFetching: campaignsSelectors.isCampaignFetching(state),
    isCampaignTargetsFetching: campaignsSelectors.isCampaignTargetsFetching(state),
    isPending: campaignsSelectors.isPending(state),
  }));

  const [activeActionCenters, setActiveActionCenters] = useState(null);
  const [status, setStatus] = useState(0);
  const [tabIndex, setTabIndex] = useState('actionCenters');

  const { actionCenters = [], name } = campaignDetails;

  const breadcrumbs = [
    {
      link: `/${organizationUuid}/campaigns`,
      name: 'Advocacy',
    },
    {
      link: '',
      name,
    },
  ];

  useEffect(() => {
    let filteredActionCenters = actionCenters;

    if (status) {
      filteredActionCenters = filteredActionCenters.filter(
        ({ publishStatus }) => status === publishStatus
      );
    }

    setActiveActionCenters(filteredActionCenters);
  }, [campaignDetails, status]);

  useEffect(() => {
    dispatch(fetchCampaign(campaignUuid));
  }, [dispatch, campaignUuid]);

  const handleChangeTab = newValue => {
    setTabIndex(newValue);

    if (newValue === 'targets' && !firstFetch.current.targets) {
      dispatch(fetchCampaignTargets(campaignUuid)).then(() => {
        firstFetch.current.targets = true;
      });
    }

    if (newValue === 'advocates' && !firstFetch.current.advocates) {
      dispatch(fetchAdvocates(campaignUuid)).then(() => {
        firstFetch.current.advocates = true;
      });
    }
  };

  const campaignTabs = [
    {
      label: 'Action Centers',
      value: 'actionCenters',
    },
    {
      label: 'Targets',
      value: 'targets',
    },
    {
      label: 'Advocates',
      value: 'advocates',
    },
    {
      label: 'Reports',
      value: 'reports',
    },
  ];

  const renderActionCenters = () =>
    activeActionCenters?.length ? (
      activeActionCenters.map(actionCenter => (
        <ActionCenterItem actionCenter={actionCenter} key={actionCenter.uuid} />
      ))
    ) : (
      <Card variant="outlined" square>
        <StyledTypography>No Action Centers for selected status</StyledTypography>
      </Card>
    );

  if (isCampaignFetching) {
    return (
      <CircularProgressWrapper>
        <CircularProgress />
      </CircularProgressWrapper>
    );
  }

  return (
    <>
      <BreadcrumbsNavigation links={breadcrumbs} />
      <Container>
        <Grid container>
          <CampaignTopCard />
        </Grid>
        <Grid container>
          <CampaignOverview />
        </Grid>
        {actionCenters?.length ? (
          <CampaignTableWrapper>
            <StyledPaper square variant="outlined">
              <Tabs handleSetTab={handleChangeTab} tabs={campaignTabs} tabIndex={tabIndex} />
            </StyledPaper>
            <TabPanel index={campaignTabs[0].value} value={tabIndex}>
              <StatusCardWrapper square variant="outlined">
                <StyledSelectLabel id="action-center-status-label" shrink>
                  Status
                </StyledSelectLabel>
                <StyledSelect
                  labelId="action-center-status-label"
                  onChange={event => setStatus(event.target.value)}
                  value={status}
                >
                  <MenuItem value={0}>All</MenuItem>
                  <MenuItem value={1}>Draft</MenuItem>
                  <MenuItem value={2}>Live</MenuItem>
                  <MenuItem value={3}>Stopped</MenuItem>
                </StyledSelect>
              </StatusCardWrapper>
              {actionCenters?.length ? (
                renderActionCenters()
              ) : (
                <Card variant="outlined" square>
                  <StyledTypography>No actions alerts for this campaign</StyledTypography>
                </Card>
              )}
            </TabPanel>
            <TabPanel index={campaignTabs[1].value} value={tabIndex}>
              <TargetsTable
                isDataFetching={isCampaignTargetsFetching}
                messagesLabel="Emails/Signatures"
                targets={campaignTargets}
              />
            </TabPanel>
            <TabPanel index={campaignTabs[2].value} value={tabIndex}>
              <AdvocatesTable advocatesList={advocatesList} isDataFetching={isAdvocatesFetching} />
            </TabPanel>
            <TabPanel index={campaignTabs[3].value} value={tabIndex}>
              <AnalyticsReportWrapper square variant="outlined">
                <ReportsLayout>
                  <CampaignAnalyticsReport
                    campaignUuid={campaignUuid}
                    organizationUuid={organizationUuid}
                  />
                  <CampaignMessagesReport
                    campaignUuid={campaignUuid}
                    organizationUuid={organizationUuid}
                  />
                </ReportsLayout>
              </AnalyticsReportWrapper>
            </TabPanel>
          </CampaignTableWrapper>
        ) : (
          <ButtonWrapper>
            <Button
              disableElevation
              color="secondary"
              component={Link}
              startIcon={<AddIcon />}
              to={`/${organizationUuid}/create-action-center/${campaignUuid}`}
              variant="contained"
            >
              start first action center
            </Button>
          </ButtonWrapper>
        )}
        <Backdrop isOpen={isPending} />
      </Container>
    </>
  );
};

export default CampaignDetails;
